import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import API from "Boards/API";
import { BoardContext } from "Boards/BoardContext";
import { reportsKeys } from "Reports/SavedReports/API/keys";

import FnrButton from "UI/FnrButton3";
import FnrInput from "UI/FnrInput";

import ButtonTooltip from "./ButtonTooltip";
import useBoardLocation from "./hooks/useBoardLocation";

export default function BoardTitle() {
  const shareIconRef = useRef();

  const [{ boardTitle, boardID, groupURL, isShared }, dispatch] =
    useContext(BoardContext);

  const { isReportBoard } = useBoardLocation();

  const [tempTitle, setTempTitle] = useState(boardTitle);
  const [isEditing, setIsEditing] = useState(false);
  const [renamingBoard, setRenamingBoard] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  const queryClient = useQueryClient(0);

  useEffect(() => {
    //validate the title
    if (tempTitle.trim().length <= 2) return setCanSave(false);
    return setCanSave(true);
  }, [tempTitle, boardTitle]);

  useEffect(() => {
    if (!renamingBoard) return;
    if (!canSave) return setRenamingBoard(false);

    const renameCall = API.renameBoard({
      boardID,
      title: tempTitle.trim(),
      groupURL,
    });

    renameCall
      .post()
      .then((resp) => {
        if (resp.url) {
          // for now, redirect to new URL
          window.location = `/${groupURL}/board/${resp.url}`;
          return;
        }
        if (isReportBoard) {
          dispatch({ type: "Set boardTitle", payload: tempTitle });
          setRenamingBoard(false);
          setIsEditing(false);
          queryClient.invalidateQueries(reportsKeys.getReportsList());
        }
      })
      .catch((resp) => {
        setRenamingBoard(false);
        if (resp.error === "title-exists")
          alert(
            "It looks like there's another board on this group with that name!  Can you pick another name?"
          );
        else alert("Something went wrong");
      });

    return () => renameCall.cancel();

    // Aug 2021 → we’re doing a clean slate of all eslint warnings- this is disabled because we don't have time to look at it.
    // eslint-disable-next-line
  }, [renamingBoard]);

  const saveTitle = () => {
    if (tempTitle.trim() === boardTitle) cancelEdit();
    else setRenamingBoard(true);
  };

  const editTitle = () => {
    setTempTitle(boardTitle);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveTitle();
    if (e.key === "Escape") cancelEdit();
  };

  useEffect(() => {
    if (!isEditing) setTempTitle(boardTitle);
  }, [isEditing, boardTitle]);

  return (
    <BoardTitleWrapper
      data-cy="boardTitle"
      $isReportsBoard={isReportBoard}
      key={boardID} // this key is to reset isEditing when switching between reports
    >
      {boardTitle}
      {isShared && (
        <>
          <SharedIcon
            ref={shareIconRef}
            className="fad fa-share-all"
            onMouseEnter={() => setShowShareTooltip(true)}
            onMouseLeave={() => setShowShareTooltip(false)}
          />
          <ButtonTooltip
            show={showShareTooltip}
            buttonRef={shareIconRef}
            content="This report is shared with other admins."
          />
        </>
      )}{" "}
      <EditButton data-cy="boardTitleEditButton" onClick={editTitle}>
        <div className="fas fa-pencil" />
      </EditButton>
      {isEditing && (
        <BoardEditing>
          <FnrInput
            value={tempTitle}
            onChange={setTempTitle}
            disabled={renamingBoard}
            autoFocus
            size="small"
            inputStyle={{ fontSize: 20, fontWeight: 400, paddingRight: 136 }}
            onKeyDown={handleKeyDown}
          />
          <SaveButtonsHolder>
            <FnrButton
              data-cy="editBoardTitleCancelButton"
              text="Cancel"
              size="small"
              borderRadius={4}
              disabled={renamingBoard}
              onClick={cancelEdit}
              style={{ marginRight: 4 }}
            />
            <FnrButton
              data-cy="editBoardTitleSaveButton"
              text="Save"
              color="green"
              size="small"
              borderRadius={4}
              disabled={!canSave}
              loading={renamingBoard}
              onClick={saveTitle}
            />
          </SaveButtonsHolder>
        </BoardEditing>
      )}
    </BoardTitleWrapper>
  );
}

const BoardTitleWrapper = styled.div`
  font-size: 20px;
  font-weight: 400;
  padding-left: 2px;
  position: relative;
  display: flex;
  align-items: center;
  /* margin-left: ${(p) => (p.$isReportsBoard ? "128px" : "0")}; */
`;
const SharedIcon = styled.span`
  margin-left: 6px;
  cursor: help;
`;
const BoardEditing = styled.div`
  position: absolute;
  top: 0;
  margin-top: -8px;
  margin-left: -10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  width: calc(100% + 200px);
`;
const EditButton = styled.div`
  margin-left: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
  opacity: 0;
  color: #278dc1;
  border-radius: 24px;
  height: 24px;
  width: 24px;
  line-height: 24px;
  text-align: center;
  ${BoardTitleWrapper}:hover & {
    opacity: 1;
  }
  &:hover {
    background: #278dc1;
    color: #fff;
    transform: scale(1.2);
  }
  &:active {
    transform: scale(1);
  }
`;
const SaveButtonsHolder = styled.div`
  position: absolute;
  right: 4px;
  display: flex;
`;
